export class RequestError extends Error {
  public statusCode: string;
  public url: string;
  public location: string;
  public method: string;
  public timestamp: Date;

  constructor(statusCode: string, url: string, method: string) {
    super(`Invalid response code: ${statusCode}`);
    this.name = 'RequestError';
    this.statusCode = statusCode;
    this.url = url;
    this.location = window.location.href;
    this.method = method;
    this.timestamp = new Date();
  }

  toString() {
    return (
      `${this.stack}\n` +
      `Failing request: ${this.method} ${this.url}\n` +
      `Current page: ${this.location}\n` +
      `Timestamp: ${this.timestamp.toJSON()}`
    );
  }
}

export class NotFoundError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'NotFoundError';
  }
}

export class PermissionsError extends Error {
  constructor() {
    super('403');
    this.name = 'PermissionsError';
  }
}

export class AbortError extends Error {
  constructor() {
    super('Promise aborted.');
    this.name = 'AbortError';
  }
}

/** Extracts the HTTP status code from a Smithy client error's $metadata. */
export function getHttpStatusCode(error: unknown): number | undefined {
  return (error as Error & { $metadata?: { httpStatusCode?: number } })?.$metadata?.httpStatusCode;
}

/**
 * Returns the server's own message for a rejected request, or undefined when there is no useful one
 * to show.
 *
 * Only client errors (4xx) qualify, because those carry an explanation the user can act on — a shift
 * being full, or its signup deadline having passed. Server errors, timeouts and network failures
 * produce text that means nothing to the user, so callers should fall back to their own copy.
 */
export function getApiErrorMessage(error: unknown): string | undefined {
  const statusCode = getHttpStatusCode(error);
  if (statusCode === undefined || statusCode < 400 || statusCode >= 500) {
    return undefined;
  }
  return (error as Error)?.message?.trim() || undefined;
}
